import { memo, useState } from 'react';
import { useDndContext, useDndMonitor, useDraggable, useDroppable } from '@dnd-kit/core';
import { BookImage, MoreVertical, Users } from 'lucide-react';
import {
  DeleteNodeMenuItem,
  DownloadZipMenuItem,
  FavoriteNodeMenuItem,
  RenameNodeMenuItem,
  ShareAlbumMenuItem,
  UploadToAlbumMenuItem,
} from '@/components/menu-items/node-menu-items';
import {
  contextMenuOpenStyles,
  dropStyle,
  hoverStyles,
  selectedStyles,
  selectedTreeItemIconStyles,
  styles,
  treeItemIconStyles,
} from '@/components/sidebar/sidebar';
import { MenuProvider, MenuSeparator, contextPrimitives, dropdownPrimitives } from '@/components/menu-renderer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useBatchUpdateAssets } from '@/hooks/data/assets/useBatchUpdateAssets';
import { type IngestionAlbum } from '@/hooks/data/inspector/useUploadStore';
import { DAMNodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { AddAssetCommand } from '@/hooks/commands/albums/AddAssetCommand';
import { DEPTH_PADDING, TreeMode } from '@/components/sidebar/tree/tree';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useAlbumAssets } from '@/hooks/data/albums/useAlbumAssets';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCommandContext } from '@/context/CommandContext';
import { ObjectAction, ObjectType } from '@/types/batch';
import { useTreeContext } from '@/context/TreeContext';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { DraggableItems } from '@/types/dragDrop';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type AlbumProps = {
  treeBranch: TreeRecursiveChildrenNode;
  parentId?: string;
  depth: number;
  mode?: TreeMode;
  disableDrag?: boolean;
  onSelectAlbum?: () => void;
  selectedAlbums?: Array<IngestionAlbum>;
  isSearchResult?: boolean;
};

const AlbumComponent = ({
  treeBranch,
  parentId,
  depth,
  disableDrag = false,
  onSelectAlbum,
  mode,
  selectedAlbums,
  isSearchResult = false,
}: AlbumProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const {
    id,
    node: { name, id: albumId },
    shared,
  } = treeBranch;
  const { addAssetsMutation, removeAssetsMutation } = useAlbumAssets();
  // One of these per sidebar row, so a bare subscription re-renders every row's tooltip and menu.
  const setIsAllSelected = useBrowserStore((state) => state.setIsAllSelected);
  const isAllSelected = useBrowserStore((state) => state.isAllSelected);
  const { includeSubFolderAssets } = useTreeContext();
  const selectedNode = useTreeStore((state) => state.selectedNode);
  const reusableTreeSelection = useTreeStore((state) => state.reusableTreeSelection);
  const { selectedAssets, deselectAllItems } = useGallerySelection();
  const { batchUpdateAssetsMutation } = useBatchUpdateAssets();
  const { apply } = useCommandContext();
  const { toast } = useToast();
  const { active: draggedItem, over: overItem } = useDndContext();
  const tToasts = useTranslations('toasts');
  const tMenu = useTranslations('menu.album');
  const tDialogs = useTranslations('dialogs');
  const tCommon = useTranslations('common');
  const tTree = useTranslations('tree');

  const openModal = useOpenModal();
  const formattedDragAlbumId = mode ? `${mode}-${albumId}` : `sidebar-${albumId}`;
  const formattedDragId = mode ? `${mode}-${id}` : `sidebar-${id}`;

  const { setNodeRef, isOver } = useDroppable({
    id: formattedDragAlbumId,
    data: {
      name: name,
      type: DraggableItems.ALBUM,
      id: albumId,
      parentId: parentId,
      accepts: [DraggableItems.ASSET],
    },
  });

  const showOverStyles =
    isOver &&
    (draggedItem?.data?.current?.id === overItem?.data?.current?.id ||
      [DraggableItems.ASSET].includes(draggedItem?.data?.current?.type));

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: formattedDragId,
    data: {
      type: DraggableItems.ALBUM,
      id,
      name,
      shared,
      parentId,
      renderDragOverlay: ({ active }: { active: { id: string; data: { current: { shared: boolean } } } }) => (
        <div
          id={active.id}
          className={cn(
            'flex cursor-pointer items-center rounded-md bg-highlight-base py-1 pr-2 text-text-inverted opacity-50',
            isSearchResult ? 'pl-2' : 'pl-8',
          )}
        >
          <BookImage className={cn(treeItemIconStyles, selectedTreeItemIconStyles)} strokeWidth={2} />
          <div className="ml-2 flex w-full justify-between">
            <span className="line-clamp-1 text-left text-base break-all">{name}</span>
          </div>
          {active.data.current.shared && <Users className="ml-2 inline-block icon-4" strokeWidth={2.5} />}
        </div>
      ),
    },
    disabled: disableDrag,
  });

  useDndMonitor({
    onDragEnd(event) {
      void (async () => {
        const { active, over } = event;

        if (
          over?.id === formattedDragAlbumId &&
          over.data.current?.accepts?.includes(active.data.current?.type) &&
          over?.data?.current?.type === DraggableItems.ALBUM &&
          active?.data?.current?.type === DraggableItems.ASSET
        ) {
          // Check if the whole album is selected.
          if (isAllSelected) {
            openModal(ModalId.Confirm, {
              content: tDialogs.rich('confirmMoveOrAddAllAssets', {
                type: 'album',
                bold: (chunks) => <span className="font-bold">{chunks}</span>,
              }),
              confirmButtonLabel: tCommon('add'),
              confirmButtonVariant: 'success',
              confirmButtonDataCy: 'album-add-all-assets-confirm',
              dismissButtonDataCy: 'album-add-all-assets-cancel',
              onConfirm: async () => {
                try {
                  await batchUpdateAssetsMutation({
                    objectType: ObjectType.Album,
                    objectAction: ObjectAction.Album_Asset_Update,
                    filter: {
                      ...(selectedNode?.type === DAMNodeType.Album
                        ? { albumId: active.data.current?.parentId }
                        : { folderId: active.data.current?.parentId, includeSubFolderAssets }),
                    },
                    payload: {
                      albumId: albumId,
                      add: true,
                    },
                  });

                  toast({
                    title: tToasts('asset.added.title'),
                    descriptionClassName: 'flex min-w-0 whitespace-pre',
                    description: tToasts.rich('asset.added.description', {
                      name,
                      bold: (chunks) => <ToastHighlight chunks={chunks} />,
                    }),
                  });

                  deselectAllItems();
                  setIsAllSelected(false);
                } catch (error) {
                  errorLog('Action failed:', error);

                  toast({
                    title: tToasts('asset.addFailed.title'),
                    variant: ToastVariant.DESTRUCTIVE,
                    descriptionClassName: 'flex min-w-0 whitespace-pre',
                    description: tToasts.rich('asset.addFailed.description', {
                      name,
                      bold: (chunks) => <ToastHighlight chunks={chunks} />,
                    }),
                  });
                }
              },
            });
            return;
          }

          let assetIds = [active.data?.current?.id];
          if (active.data?.current?.type === DraggableItems.ASSET && selectedAssets?.length > 1) {
            assetIds = selectedAssets.map((asset) => asset.id);
          }

          const addAssetCommand = AddAssetCommand(addAssetsMutation, removeAssetsMutation, albumId, assetIds);
          await apply(addAssetCommand);
        }
      })();
    },
  });

  const selectedAlbumIds = selectedAlbums?.map((album) => album.id);
  const alreadySelected = mode === TreeMode.SelectAlbum && selectedAlbumIds?.includes(albumId);
  const inDialog = Boolean(mode);
  const isNodeSelected = selectedNode?.node.id === albumId;
  const isAlbumSelected = selectedNode?.type === DAMNodeType.Album && isNodeSelected;
  const isReusableSelection = mode === TreeMode.SelectAlbum && reusableTreeSelection?.id === albumId;
  const shouldApplySelectedStyles = (isNodeSelected || isReusableSelection) && !alreadySelected;
  const shouldHighlightFolderIcon = ((!inDialog && isAlbumSelected) || isReusableSelection) && !alreadySelected;

  return (
    <div
      className="flex h-full flex-1 flex-col gap-y-1.5 focus-within:transition-colors focus-within:duration-150"
      title={name}
    >
      <div
        ref={setNodeRef}
        style={{
          paddingLeft: `${depth * DEPTH_PADDING + 8}px`,
        }}
        className={cn(
          'group/tree-item relative flex w-full items-center pr-2 h-full',
          styles,
          isDragging ? 'z-1 opacity-80' : 'z-0 opacity-100',
          {
            [hoverStyles]: !isDragging && !showOverStyles,
            [contextMenuOpenStyles]: !shouldApplySelectedStyles,
            [selectedStyles]: shouldApplySelectedStyles,
          },
        )}
      >
        <div
          className={cn('flex w-full items-center', {
            [dropStyle]: showOverStyles || alreadySelected,
          })}
          {...(!disableDrag && {
            ref: setDragRef,
            ...listeners,
            ...attributes,
          })}
          tabIndex={-1}
        >
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <button
                className={cn(
                  'mr-0 flex w-full flex-row items-center py-0.5 pr-2 outline-hidden duration-150 group-hover/tree-item:mr-7 focus-visible:ring-2 pl-1 rounded-md group-has-[[aria-haspopup]:focus-visible]/tree-item:mr-7',
                  menuOpen ? 'mr-7' : 'mr-0',
                  isSearchResult ? 'ml-2' : 'ml-7',
                  shouldApplySelectedStyles
                    ? 'focus-visible:ring-text-inverted'
                    : 'focus-visible:ring-highlight-hover-color',
                )}
                onClick={onSelectAlbum}
              >
                <BookImage
                  className={cn(
                    'mr-2',
                    treeItemIconStyles,
                    shouldHighlightFolderIcon ? selectedTreeItemIconStyles : 'stroke-album-color',
                  )}
                  strokeWidth={2}
                />
                <div className="flex w-full justify-between">
                  <span
                    className={cn(
                      'line-clamp-1 break-all text-left text-base duration-150',
                      shared && 'mr-6 transform duration-150',
                    )}
                  >
                    {name}
                  </span>
                </div>
                {shared && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Users
                        className={cn(
                          'absolute right-4 inline-block duration-150 ease-out icon-4 group-hover/tree-item:right-11 group-has-[[aria-haspopup]:focus-visible]/tree-item:right-11',
                          shouldHighlightFolderIcon ? 'stroke-text-inverted' : 'stroke-icon-base',
                          { 'right-11': menuOpen },
                        )}
                        strokeWidth={2.5}
                      />
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent>{tTree('sharedAlbum')}</TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                )}
              </button>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <MenuProvider primitives={contextPrimitives}>
                <RenameNodeMenuItem node={treeBranch} />
                <DeleteNodeMenuItem node={treeBranch} danger />
                {!inDialog && (
                  <>
                    <MenuSeparator />
                    <FavoriteNodeMenuItem node={treeBranch} />
                    <MenuSeparator />
                    <ShareAlbumMenuItem node={{ id: albumId, name }} />
                    <MenuSeparator />
                    <UploadToAlbumMenuItem albumId={albumId} albumName={name} />
                    <DownloadZipMenuItem nodeIds={[albumId]} name={name} sourceType={DAMNodeType.Album} />
                  </>
                )}
              </MenuProvider>
            </ContextMenuContent>
          </ContextMenu>
          <DropdownMenu
            onOpenChange={(isOpen) => {
              setMenuOpen(isOpen);
              if (isOpen && onSelectAlbum) {
                onSelectAlbum();
              }
            }}
          >
            <Tooltip delayDuration={1000}>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger
                  className={cn(
                    'group/album-options pointer-events-none absolute right-2 mr-2 rounded-sm outline-hidden duration-150 opacity-0 group-hover/tree-item:pointer-events-auto group-hover/tree-item:opacity-100 focus-visible:opacity-100 focus-within:ring-2',
                    menuOpen ? 'opacity-100' : '',
                    shouldApplySelectedStyles
                      ? 'focus-visible:ring-text-inverted'
                      : 'focus-visible:ring-highlight-hover-color',
                  )}
                  data-cy="album-options-trigger"
                >
                  <MoreVertical
                    className={cn(
                      'icon-5 duration-150 group-hover/tree-item:opacity-100 rotate-90 scale-0 group-hover/tree-item:rotate-0 group-hover/tree-item:scale-100',
                      shouldHighlightFolderIcon ? 'stroke-text-inverted' : 'stroke-icon-base',
                      menuOpen
                        ? 'rotate-0! scale-100! opacity-100'
                        : 'opacity-0 group-focus-visible/album-options:rotate-0! group-focus-visible/album-options:scale-100! group-focus-visible/album-options:opacity-100!',
                    )}
                    strokeWidth={2}
                  />
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>{tMenu('options')}</TooltipContent>
              </TooltipPortal>
            </Tooltip>
            <DropdownMenuContent>
              <MenuProvider primitives={dropdownPrimitives}>
                <RenameNodeMenuItem node={treeBranch} />
                <DeleteNodeMenuItem node={treeBranch} danger />
                {!inDialog && (
                  <>
                    <MenuSeparator />
                    <FavoriteNodeMenuItem node={treeBranch} />
                    <MenuSeparator />
                    <ShareAlbumMenuItem node={{ id: albumId, name }} />
                    <MenuSeparator />
                    <UploadToAlbumMenuItem albumId={albumId} albumName={name} />
                    <DownloadZipMenuItem nodeIds={[albumId]} name={name} sourceType={DAMNodeType.Album} />
                  </>
                )}
              </MenuProvider>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export const Album = memo(AlbumComponent);
